import { refreshToken } from "../pages/LoginPage/requests/loginRequests";
import {
  saveAuthData,
  getMillisecondsUntilExpiry,
  isUserAuthenticated,
  getMillisecondsUntilRefreshExpiry,
} from "./authUtils";
import {
  handleSessionExpired,
  registerStopTokenRefresh,
} from "./sessionExpired";

let refreshIntervalId: NodeJS.Timeout | null = null;

const REFRESH_BEFORE_MS = 5 * 60 * 1000;
const MIN_REFRESH_RETRY_MS = 1000;
const MAX_REFRESH_RETRY_MS = 30000;

let refreshInFlight = false;
let invalidExpiryRetryCount = 0;
let refreshEnabled = false;

const clearCurrentRefreshTimer = () => {
  if (refreshIntervalId !== null) {
    clearTimeout(refreshIntervalId);
    refreshIntervalId = null;
  }
};

export const startTokenRefresh = (): void => {
  stopTokenRefresh();
  refreshEnabled = true;

  const scheduleRefresh = () => {
    if (!refreshEnabled) return;
    clearCurrentRefreshTimer();

    const msUntilExpiry = getMillisecondsUntilExpiry();

    if (!msUntilExpiry) {
      const msUntilRefreshExpiry = getMillisecondsUntilRefreshExpiry();

      if (!msUntilRefreshExpiry) {
        handleSessionExpired();
        return;
      }

      const retryInMs = Math.min(
        MIN_REFRESH_RETRY_MS * 2 ** invalidExpiryRetryCount,
        MAX_REFRESH_RETRY_MS,
      );
      invalidExpiryRetryCount += 1;

      refreshIntervalId = setTimeout(async () => {
        if (!refreshEnabled) return;
        if (refreshInFlight) return;
        refreshInFlight = true;

        try {
          const response = await refreshToken();

          if (response?.success && response.data) {
            saveAuthData(response.data);
            scheduleRefresh();
          } else {
            handleSessionExpired();
          }
        } finally {
          refreshInFlight = false;
        }
      }, retryInMs);

      return;
    }

    invalidExpiryRetryCount = 0;
    const refreshInMs = Math.max(msUntilExpiry - REFRESH_BEFORE_MS, 1000);

    refreshIntervalId = setTimeout(async () => {
      if (!refreshEnabled) return;
      if (refreshInFlight) return;
      refreshInFlight = true;

      try {
        const response = await refreshToken();

        if (response?.success && response.data) {
          const refreshedData = response.data;
          saveAuthData(refreshedData);
          scheduleRefresh();
        } else {
          handleSessionExpired();
        }
      } finally {
        refreshInFlight = false;
      }
    }, refreshInMs);
  };

  scheduleRefresh();
};

export const stopTokenRefresh = (): void => {
  refreshEnabled = false;
  clearCurrentRefreshTimer();
  invalidExpiryRetryCount = 0;
};

registerStopTokenRefresh(stopTokenRefresh);

export const initializeTokenRefresh = (): void => {
  if (isUserAuthenticated()) {
    startTokenRefresh();
  } else {
    stopTokenRefresh();
  }
};
